import dayjs from "dayjs";
import React, { HTMLAttributes, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getPaymentBySubcriptionToken } from "../../apis/payment";
import {
  adyenCreditCardSingleCheckout,
  generateSubscriptionReceipt,
} from "../../apis/subscription";
import RefreshIcon from "../../assets/icons/refetch.png";
import BaseDarkButton from "../../components/atom/baseDarkButton";
import BaseTitle from "../../components/atom/baseTitle";
import SinglePaymentMethodSelectionBlock from "../../components/molecules/singlePaymentMethodSelectionBlock";
import { convertBase64ToPdf } from "../../helper/convertor";
import { toHK } from "../../helper/format";
import { customShowToast } from "../../helper/toast";
import { ROUTE_PATHS } from "../../routes/paths/route-paths";
import useGeneralStore from "../../store/general";
import useSubscriptionPaymentStore from "../../store/subscriptionPayment";

export const PAYMENT_STATE_COLOR_MAPPING: Record<number, { color: string }> = {
  1: { color: "text-mainYellow" },
  3: { color: "text-orange-500" },
  4: { color: "text-green-500" },
  5: { color: "text-alertRed" },
  7: { color: "text-orange-500" },
};

interface ISubscriptionPaymentPage {}

type TSubscriptionPaymentPageProps = ISubscriptionPaymentPage &
  HTMLAttributes<HTMLDivElement>;

const SubscriptionPaymentPage: React.FC<TSubscriptionPaymentPageProps> = (
  props
) => {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const query_payment_id = queryParams.get("payment_id");
  const resultCode = queryParams.get("resultCode");

  const { ...otherProps } = props;
  const navigate = useNavigate();
  const setGlobalLoadingState = useGeneralStore((state) => state.setIsLoading);
  const { subscriptionPaymentList, setsubscriptionPaymentList } =
    useSubscriptionPaymentStore((state) => state);

  const fetchPaymentHistory = async () => {
    try {
      setGlobalLoadingState(true);
      setsubscriptionPaymentList(null);
      const loginState = JSON.parse(
        localStorage.getItem("wbloginState") || "{}"
      );
      const userToken = loginState.t;
      const payments = await getPaymentBySubcriptionToken(token!, userToken);
      // @ts-ignore
      if (payments.message === "access_denied") {
        customShowToast("權限不足，正在為你轉跳..", "error", () => {
          window.location.href = ROUTE_PATHS.INQUIRE_PAGE;
        });
      } else {
        if (payments) {
          setsubscriptionPaymentList(payments);
          setGlobalLoadingState(false);
        }
      }
    } catch (error) {
      setGlobalLoadingState(false);
      setsubscriptionPaymentList(null);
      customShowToast("未知錯誤，正在為你轉跳..", "error", () => {
        window.location.href = ROUTE_PATHS.INQUIRE_PAGE;
      });
    } finally {
      setGlobalLoadingState(false);
    }
  };

  const refreshPaymentHistoryHandler = async () => {
    console.log("trigger refresh payment history");
    if (query_payment_id) {
      const targetPayment = subscriptionPaymentList?.find(
        (item) => item.id.toString() === query_payment_id
      );

      if (
        resultCode === null ||
        (targetPayment?.payment_state_id === 4 &&
          resultCode === "authorised") ||
        ((targetPayment?.payment_state_id === 1 ||
          targetPayment?.payment_state_id === 5) &&
          resultCode !== "authorised")
      ) {
        console.log(`remove all query string and reload page`);
        // remove all query string and reload page
        const _path = window.location.pathname;

        navigate(_path, {
          replace: true,
          state: {
            refresh: true,
          },
        });
      }
      fetchPaymentHistory();
    } else {
      fetchPaymentHistory();
    }
  };

  const adyenCreditCardCheckoutHandler = async (payment_id: number) => {
    try {
      setGlobalLoadingState(true);

      const checkoutResult = await adyenCreditCardSingleCheckout(payment_id);
      if (checkoutResult.message === "card_not_found") {
        customShowToast("信用卡資料不正確，請重新綁定", "error");
      } else if (checkoutResult.message === "Refused") {
        customShowToast("付款失敗", "error");
      } else {
        customShowToast("付款成功,稍後將為您處理", "success", () => {
          fetchPaymentHistory();
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setGlobalLoadingState(false);
    }
  };

  const downloadReceiptHandler = async (paymentId: number) => {
    try {
      setGlobalLoadingState(true);
      const loginState = JSON.parse(
        localStorage.getItem("wbloginState") || "{}"
      );
      const userToken = loginState.t;
      const response = await generateSubscriptionReceipt(paymentId, userToken);
      if (response) {
        convertBase64ToPdf(response.data, response.fileName ?? "receipt");
        customShowToast(`已生成收據`, "success");
      }
      setGlobalLoadingState(false);
    } catch (error) {
      console.error(error);
    } finally {
      setGlobalLoadingState(false);
    }
  };

  useEffect(() => {
    const loginState = JSON.parse(localStorage.getItem("wbloginState") || "{}");

    if (loginState.t) {
      fetchPaymentHistory();
    } else {
      // if no token, redirect to landing page

      navigate(ROUTE_PATHS.LANDING_PAGE);
    }
  }, [token]);

  return (
    <div className="flex flex-col gap-4 px-4 my-4" {...otherProps}>
      <div className="flex flex-row justify-between items-center">
        <BaseTitle text="賬單記錄" />
        <button
          className="flex flex-row items-center"
          onClick={refreshPaymentHistoryHandler}
        >
          <div className="text-white text-xs">刷新</div>
          <img src={RefreshIcon} width={14} height={14} />
        </button>
      </div>

      {subscriptionPaymentList === null && (
        <div className="flex flex-col gap-4 mb-4">
          {[1, 2, 3].map((item) => {
            return (
              <div
                key={item}
                className="flex flex-col justify-center skeleton h-20 w-full p-4"
              ></div>
            );
          })}
        </div>
      )}

      <div className="text-white text-sm">
        {subscriptionPaymentList &&
          subscriptionPaymentList?.length > 0 &&
          subscriptionPaymentList.map((item, index) => {
            const unpaid = item.payment_state_id === 1;

            const paid = item.payment_state_id === 4;
            const fullRefunded = item.payment_state_id === 7;
            // const isActiveMonthlyOrder =
            //   item.monthlyOrder.monthly_order_state_id === 2;

            // console.log("payment id ", item.id);

            const intervalCount =
              item.monthlyOrder.monthlyOrderItem.interval_count;
            const monthsDiff = dayjs(item.next_pay_time).diff(dayjs(), "month"); // integer months
            const canPay = monthsDiff < intervalCount;

            // console.log(`intervalCount`);
            // console.log(intervalCount);
            // console.log(`monthsDiff`);
            // console.log(monthsDiff);
            // console.log(`canPay`);
            // console.log(canPay);

            // console.log(`id: ${item.id}`);
            // console.log(dayjs(item.next_pay_time).diff(dayjs(), "month", true));
            // console.log(intervalCount);

            // const monthsDiff = dayjs(item.next_pay_time).diff(
            //   dayjs(),
            //   "month",
            //   true
            // );

            // const canPay = monthsDiff < intervalCount;

            const isWaitingForWebhook =
              query_payment_id === item.id.toString() &&
              resultCode === "authorised";

            // console.log(`isWaitingForWebhook`);
            // console.log(isWaitingForWebhook);

            const color = PAYMENT_STATE_COLOR_MAPPING[item.payment_state_id];
            return (
              <div className="collapse collapse-arrow mb-5" key={index}>
                <input type="checkbox" className="peer" />
                <div className="collapse-title bg-[#0D1014]">
                  <div className="flex flex-row gap-2 text-[12px] justify-between">
                    <div className="flex flex-col gap-1">
                      <div>編號 {item.ref_no}</div>

                      {/*  paid time */}
                      {item.paidAt && (
                        <div className="font-bold text-green-500">
                          付款日期{" "}
                          {toHK(item.paidAt).format("YYYY-MM-DD HH:mm")}
                        </div>
                      )}

                      {fullRefunded && item.fail_date && (
                        <div className="font-bold text-orange-500">
                          退款日期{" "}
                          {toHK(item.fail_date).format("YYYY-MM-DD HH:mm")}
                        </div>
                      )}

                      {/* next pay time */}
                      {item.next_pay_time && unpaid && (
                        <div className="font-bold text-mainYellow">
                          自動扣款日期{" "}
                          {toHK(item.next_pay_time).format("YYYY-MM-DD")}
                        </div>
                      )}
                    </div>

                    <div className={color.color}>
                      {isWaitingForWebhook
                        ? `處理中`
                        : item.paymentState.display_name}
                    </div>
                  </div>
                </div>

                <div className="collapse-content bg-mainDark text-white text-xs">
                  {/* display subscription items */}
                  <div className="mt-4 border-b mb-2 font-bold">訂閱項目</div>
                  <div className="flex flex-row justify-between items-center mb-1">
                    <span>{item.monthlyOrder.monthlyOrderItem.name}</span>
                    <span>HKD${item.main_car_plate_price.price}</span>
                  </div>
                  {item.amount - item.main_car_plate_price.price > 0 && (
                    <div className="flex flex-row justify-between items-center">
                      <span>一位多泊</span>
                      <span>
                        HKD${item.amount - item.main_car_plate_price.price}
                      </span>
                    </div>
                  )}

                  {/* date section */}
                  <div className="flex flex-col gap-1 text-xs items-end mt-5">
                    <div>
                      開始日期:{" "}
                      {toHK(item.period_start_date).format("YYYY-MM-DD")}
                    </div>

                    <div>
                      完結日期:{" "}
                      {toHK(item.period_end_date).format("YYYY-MM-DD")}
                    </div>

                    {item.fail_date && unpaid && (
                      <div className="text-alertRed">
                        付款失敗日期:{" "}
                        {toHK(item.fail_date).format("YYYY-MM-DD HH:mm")}
                      </div>
                    )}

                    {item.fail_reason && unpaid && (
                      <div className="text-alertRed">
                        付款失敗原因: {item.fail_reason}
                      </div>
                    )}

                    {item.paidAt && (
                      <div>
                        付款方式: {item.paymentMethod.display_name}{" "}
                        {item.paymentMethod.name === "credit_card" &&
                        item.payment_card_last4
                          ? `${item.payment_card_last4}`
                          : ""}
                      </div>
                    )}
                  </div>

                  <div className="border-b bg-white my-2"></div>
                  <div className="text-sm font-bold text-right mb-3">
                    總數{" "}
                    <span className="text-lg text-mainYellow">
                      HKD${item.amount - (item.amount_refunded ?? 0)}
                    </span>
                  </div>

                  <div className="flex flex-row flex-1 justify-between items-center gap-4">
                    {(paid || fullRefunded) && !isWaitingForWebhook && (
                      <BaseDarkButton
                        onClick={(e) => downloadReceiptHandler(item.id)}
                        className="flex-1 "
                      >
                        下載賬單
                      </BaseDarkButton>
                    )}

                    {/* pre payment button */}
                    {unpaid && !isWaitingForWebhook && (
                      <SinglePaymentMethodSelectionBlock
                        adyenCheckout={() => {
                          adyenCreditCardCheckoutHandler(item.id);
                        }}
                        paymentId={item.id.toString()}
                        buttonDisabled={false}
                        reInitPaymentMethod={true}
                        bindedCard={
                          item.monthlyOrder.storedPaymentMethodId
                            ? {
                                shopperReference: `monthlyOrderId-${item.monthlyOrder.id}`,
                                storedPaymentMethodId:
                                  item.monthlyOrder.storedPaymentMethodId,
                              }
                            : undefined
                        }
                        hideCreditCard={
                          !item.monthlyOrder.storedPaymentMethodId
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default SubscriptionPaymentPage;
